
import React, { useState, useEffect } from 'react';
import { Grid, TextField, Button, Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { createBill, updateBill, getBillById, listCompanies } from '../../api';
import img from '../../assets/banner.jpg';

const BillForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!(location.state?.data || id);

  const frequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'halfyearly', label: 'Half-Yearly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const [formData, setFormData] = useState({
    company_name: '',
    covered_location: '',
    covered_entity: '',
    account_no: '',
    user_name: '',
    registered_mobile_no: '',
    registered_email: '',
    frequency: 'monthly',
    package_detail: '',
    package_value: '',
    usage_limit: '',
    amount: '',
    statement_date: '',
    due_date: '',
    paid_date: '',
    payment_link: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Fetch companies for dropdown
        const companiesData = await listCompanies();
        setCompanies(Array.isArray(companiesData) ? companiesData : []);

        // Load existing bill data if in edit mode
        const initial = location.state?.data;
        if (initial) {
          setFormData({
            company_name: initial.company || '',
            covered_location: initial.location || '',
            covered_entity: '',
            account_no: initial.account_no || '',
            user_name: initial.user_name || '',
            registered_mobile_no: initial.registered_mobile_no || '',
            registered_email: initial.registered_email || '',
            frequency: initial.frequency || 'monthly',
            package_detail: initial.package_detail || '',
            package_value: initial.package_value || '',
            usage_limit: initial.usage_limit || '',
            amount: initial.amount || '',
            statement_date: initial.statement_date ? initial.statement_date.split('T')[0] : '',
            due_date: initial.due_date ? initial.due_date.split('T')[0] : '',
            paid_date: initial.paid_date ? initial.paid_date.split('T')[0] : '',
            payment_link: initial.payment_link || ''
          });
        } else if (id) {
          const fetched = await getBillById(id);
          setFormData({
            company_name: fetched.company || '',
            covered_location: fetched.location || '',
            covered_entity: '',
            account_no: fetched.account_no || '',
            user_name: fetched.user_name || '',
            registered_mobile_no: fetched.registered_mobile_no || '',
            registered_email: fetched.registered_email || '',
            frequency: fetched.frequency || 'monthly',
            package_detail: fetched.package_detail || '',
            package_value: fetched.package_value || '',
            usage_limit: fetched.usage_limit || '',
            amount: fetched.amount || '',
            statement_date: fetched.statement_date ? fetched.statement_date.split('T')[0] : '',
            due_date: fetched.due_date ? fetched.due_date.split('T')[0] : '',
            paid_date: fetched.paid_date ? fetched.paid_date.split('T')[0] : '',
            payment_link: fetched.payment_link || ''
          });
        }
      } catch (err) {
        setError('Failed to load bill data or companies');
        console.error('Error loading bill data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadFormData();
  }, [id, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };

    // Auto-calculate Amount: Package Value + (Package Value * Usage Limit / 100)
    const packageValue = parseFloat(updatedFormData.package_value) || 0;
    const usageLimit = parseFloat(updatedFormData.usage_limit) || 0;
    updatedFormData.amount = (packageValue + (packageValue * usageLimit / 100)).toFixed(2);

    setFormData(updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await updateBill(id || formData.id, formData);
      } else {
        await createBill(formData);
      }
      navigate('/view-all', {
        state: { message: isEdit ? 'Bill updated successfully!' : 'Bill created successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Submission failed');
      console.error('Error submitting bill form:', err);
    } finally {
      setLoading(false);
    }
  };

  const isDateField = (type) => type === 'date';

  if (loadingData) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${img})`,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        py: 5,
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 9,
          borderRadius: 3,
          backgroundColor: '#121212',
          color: 'text.primary',
          maxWidth: 1200,
          mx: 'auto',
          boxShadow: 3
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          {isEdit ? 'Edit Bill' : 'Utility Bill Details'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} direction="column">
            {/* First Row: Company Name */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" style={{marginRight:'185px'}}>
                  <InputLabel id="company-name-label" sx={{ color: '#faf8f6ff' }}>
                    Company Name
                  </InputLabel>
                  <Select
                    labelId="company-name-label"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      color: '#fff',
                      backgroundColor: '#1e1e1e',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company._id} value={company.company_name}>
                        {company.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Second Row: Covered Location, Covered Entity */}
            <Grid container item spacing={4}>
              {[
                { label: 'Covered Location', name: 'covered_location' },
                { label: 'Covered Entity', name: 'covered_entity' }
              ].map((field) => (
                <Grid item xs={12} sm={6} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Third Row: Account Number, User Name, Registered Mobile No., Registered Email */}
            <Grid container item spacing={4}>
              {[
                { label: 'Account Number', name: 'account_no' },
                { label: 'User Name', name: 'user_name' },
                { label: 'Registered Mobile No.', name: 'registered_mobile_no' },
                { label: 'Registered Email', name: 'registered_email' }
              ].map((field) => (
                <Grid item xs={12} sm={3} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Fourth Row: Package Detail, Package Value, Usage Limit, Frequency, Amount */}
            <Grid container item spacing={4}>
              {[
                { label: 'Package Detail', name: 'package_detail' },
                { label: 'Package Value', name: 'package_value', type: 'number' },
                { label: 'Usage Limit (%)', name: 'usage_limit', type: 'number' },
              ].map((field) => (
                <Grid item xs={12} sm={2.4} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type || 'text'}
                    value={formData[field.name]}
                    onChange={handleChange}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={2.4}>
                <FormControl fullWidth size="small" sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#ccc' },
                    '&:hover fieldset': { borderColor: '#e0b256' },
                    '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                  }
                }}>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#e0b256' } }}>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    name="frequency"
                    onChange={handleChange}
                    label="Frequency"
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  disabled
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
            </Grid>

            {/* Fifth Row: Statement Date, Due Date, Paid Date */}
            <Grid container item spacing={4}>
              {[
                { label: 'Statement Date', name: 'statement_date', type: 'date' },
                { label: 'Due Date', name: 'due_date', type: 'date' },
                { label: 'Paid Date', name: 'paid_date', type: 'date' }
              ].map((field) => (
                <Grid item xs={12} sm={4} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    inputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Sixth Row: Payment Link */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Payment Link"
                  name="payment_link"
                  value={formData.payment_link}
                  onChange={handleChange}
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
            </Grid>

            {/* Submit and Cancel Buttons */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    mt: 1.5,
                    fontWeight: 'bold',
                    backgroundColor: '#e0b256',
                    color: '#000',
                    '&:hover': { backgroundColor: '#c99e3c' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Submit'}
                </Button>
              </Grid>
              {isEdit && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.25,
                      mt: 1,
                      fontWeight: 'bold',
                      color: '#e0b256',
                      borderColor: '#e0b256',
                      '&:hover': { backgroundColor: '#222' }
                    }}
                    onClick={() => navigate('/view-all')}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default BillForm;

